import React, { useState } from "react";
import { Briefcase, Plus, Trash2, Pencil, Phone, RefreshCw } from "lucide-react";
import { Button } from "../shared/Button";
import { proveedores as api, catalogos } from "../../api/modulos.service";
import { estilos as est, Cabecera, Buscador, Vacio, Aviso, Ventana, Entrada, Seleccion, Area, useCarga } from "./base";

/**
 * PROVEEDORES.
 *
 * Un proveedor no es un visitante recurrente. El recurrente es una persona con
 * acceso a una casa; el proveedor es una empresa que trabaja para la
 * residencial entera y manda a gente distinta cada semana.
 *
 * De ahí la lista de personal: el guardia no tiene que decidir si deja pasar
 * al muchacho del uniforme, lo comprueba. Esa lista la mantiene la
 * administración, no el guardia y no la empresa.
 */

const FORM_VACIO = {
  name: "",
  trade: "Jardinería",
  contactName: "",
  phone: "",
  email: "",
  startTime: "07:00",
  endTime: "17:00",
  notes: "",
};

const DIAS = [
  { n: 0, etiqueta: "D" },
  { n: 1, etiqueta: "L" },
  { n: 2, etiqueta: "M" },
  { n: 3, etiqueta: "M" },
  { n: 4, etiqueta: "J" },
  { n: 5, etiqueta: "V" },
  { n: 6, etiqueta: "S" },
];

export default function Proveedores({ rol }) {
  const [busqueda, setBusqueda] = useState("");
  const [abierta, setAbierta] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [dias, setDias] = useState([1, 2, 3, 4, 5]);
  const [personal, setPersonal] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const { datos, cargando, error: errorCarga, recargar } = useCarga(
    () => api.listar(busqueda ? { buscar: busqueda } : {}),
    [busqueda]
  );
  const { datos: cat } = useCarga(() => catalogos(), []);
  const lista = datos?.suppliers || [];
  const esAdmin = rol === "admin";
  const rubros = cat?.rubrosProveedor || ["Jardinería", "Limpieza", "Electricidad", "Plomería", "Otro"];

  const cambiar = (c) => (e) => setForm({ ...form, [c]: e.target.value });

  const nuevo = () => {
    setEditando(null);
    setForm(FORM_VACIO);
    setDias([1, 2, 3, 4, 5]);
    setPersonal([]);
    setAbierta(true);
  };

  const editar = (p) => {
    setEditando(p);
    setForm({
      name: p.name || "",
      trade: p.trade || rubros[0],
      contactName: p.contactName || "",
      phone: p.phone || "",
      email: p.email || "",
      startTime: p.startTime || "07:00",
      endTime: p.endTime || "17:00",
      notes: p.notes || "",
    });
    setDias(p.days || []);
    setPersonal((p.staff || []).map((s) => ({ name: s.name || "", documentId: s.documentId || "", phone: s.phone || "" })));
    setAbierta(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    const cuerpo = {
      ...form,
      days: dias,
      staff: personal.filter((s) => s.name.trim()),
    };
    try {
      if (editando) await api.actualizar(editando._id, cuerpo);
      else await api.crear(cuerpo);
      setAbierta(false);
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.errors?.[0]?.msg || "No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  };

  const cambiarPersona = (i, campo) => (e) =>
    setPersonal(personal.map((s, j) => (j === i ? { ...s, [campo]: e.target.value } : s)));

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Proveedores"
        subtitulo="Las empresas que entran a trabajar, y quién viene de cada una."
        accion={esAdmin ? "Registrar proveedor" : null}
        alAccionar={nuevo}
        icono={Briefcase}
      />

      <Buscador valor={busqueda} alCambiar={setBusqueda} marcador="Buscar por empresa…" />

      {error && <Aviso>{error}</Aviso>}

      {errorCarga ? (
        <Aviso>
          {errorCarga}
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
              Reintentar
            </Button>
          </div>
        </Aviso>
      ) : cargando ? (
        <Vacio>Cargando…</Vacio>
      ) : lista.length === 0 ? (
        <Vacio>{busqueda ? "Ninguna empresa con ese nombre." : "Todavía no hay proveedores registrados."}</Vacio>
      ) : (
        <div style={est.rejilla}>
          {lista.map((p) => (
            <div key={p._id} style={{ ...est.tarjeta, opacity: p.isActive ? 1 : 0.6 }}>
              <h3 style={est.tarjetaTitulo}>{p.name}</h3>
              <div style={est.tarjetaDato}>
                {p.trade}
                {p.startTime ? ` · ${p.startTime}–${p.endTime}` : ""}
                {!p.isActive ? " · Inactivo" : ""}
              </div>
              {p.contactName && (
                <div style={est.tarjetaDato}>
                  <Phone size={14} /> {p.contactName}
                  {p.phone ? ` · ${p.phone}` : ""}
                </div>
              )}
              <div style={est.tarjetaDato}>
                {(p.days || []).length === 7
                  ? "Todos los días"
                  : (p.days || []).map((d) => DIAS[d]?.etiqueta).join(" ") || "Sin días"}
              </div>

              {(p.staff || []).length > 0 && (
                <div style={{ ...est.tarjetaDato, display: "block" }}>
                  <strong style={{ color: "var(--text)" }}>Personal autorizado: </strong>
                  {p.staff.map((s) => s.name).join(", ")}
                </div>
              )}

              {esAdmin && (
                <div style={est.tarjetaAcciones}>
                  <Button size="sm" variant="outline" leftIcon={Pencil} onClick={() => editar(p)}>
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => api.actualizar(p._id, { isActive: !p.isActive }).then(recargar)}
                  >
                    {p.isActive ? "Desactivar" : "Reactivar"}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Ventana
        titulo={editando ? `Editar «${editando.name}»` : "Registrar un proveedor"}
        abierta={abierta}
        alCerrar={() => setAbierta(false)}
        pie={
          <>
            <Button variant="ghost" onClick={() => setAbierta(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={guardar} loading={guardando}>
              Guardar
            </Button>
          </>
        }
      >
        {error && <Aviso>{error}</Aviso>}
        <Entrada etiqueta="Empresa" value={form.name} onChange={cambiar("name")} required />
        <Seleccion etiqueta="Rubro" value={form.trade} onChange={cambiar("trade")} opciones={rubros} />
        <div style={est.fila}>
          <Entrada etiqueta="Persona de contacto" value={form.contactName} onChange={cambiar("contactName")} />
          <Entrada etiqueta="Teléfono" value={form.phone} onChange={cambiar("phone")} placeholder="7777 7777" />
        </div>
        <Entrada etiqueta="Correo" type="email" value={form.email} onChange={cambiar("email")} />

        <div style={est.campo}>
          <span style={est.etiqueta}>Días en que se le espera</span>
          <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
            {DIAS.map((d) => (
              <button
                key={d.n}
                type="button"
                onClick={() => setDias(dias.includes(d.n) ? dias.filter((x) => x !== d.n) : [...dias, d.n])}
                style={{
                  minWidth: "44px",
                  minHeight: "44px",
                  borderRadius: "var(--radius-md)",
                  border: "2px solid var(--border)",
                  cursor: "pointer",
                  backgroundColor: dias.includes(d.n) ? "var(--gold)" : "var(--bg-tertiary)",
                  color: dias.includes(d.n) ? "var(--bg)" : "var(--text-secondary)",
                  fontWeight: 600,
                }}
                aria-pressed={dias.includes(d.n)}
              >
                {d.etiqueta}
              </button>
            ))}
          </div>
        </div>

        <div style={est.fila}>
          <Entrada etiqueta="Desde" type="time" value={form.startTime} onChange={cambiar("startTime")} />
          <Entrada etiqueta="Hasta" type="time" value={form.endTime} onChange={cambiar("endTime")} />
        </div>

        <span style={{ ...est.etiqueta, marginTop: "var(--space-3)" }}>Personal autorizado</span>
        {personal.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-2)" }}>
            <div style={{ flex: 2 }}>
              <Entrada etiqueta={`Nombre ${i + 1}`} value={s.name} onChange={cambiarPersona(i, "name")} />
            </div>
            <div style={{ flex: 1 }}>
              <Entrada etiqueta="DUI" value={s.documentId} onChange={cambiarPersona(i, "documentId")} />
            </div>
            <Button
              size="sm"
              variant="ghost"
              leftIcon={Trash2}
              onClick={() => setPersonal(personal.filter((_, j) => j !== i))}
              aria-label={`Quitar a la persona ${i + 1}`}
            />
          </div>
        ))}
        <Button
          size="sm"
          variant="outline"
          leftIcon={Plus}
          onClick={() => setPersonal([...personal, { name: "", documentId: "", phone: "" }])}
        >
          Agregar persona
        </Button>

        <Area etiqueta="Notas" value={form.notes} onChange={cambiar("notes")} />
      </Ventana>
    </div>
  );
}
